import type { ReactElement } from 'react';

/**
 * AutoeCTD wordmark — the canonical text-based brand mark.
 *
 * Renders: Auto + <span>e</span> + CTD + (optionally) <sup>™</sup>
 *
 * Coloring is INTENTIONALLY context-driven via the parent's CSS class
 * (`.logo` for the dark navy header, `.login-logo` for the light login
 * background) — NOT baked into this component. This lets the same DOM
 * structure read correctly against very different backgrounds without
 * a dark-mode prop.
 *
 * The <span> selector is the load-bearing piece: parent CSS targets
 * `.logo span` or `.login-logo span` to color the "e". Don't remove
 * the span even if it looks redundant.
 *
 * Usage:
 *   <Link to="/home" className="logo"><Wordmark /></Link>
 *   <h1 className="login-logo"><Wordmark /></h1>
 *   <Wordmark withTrademark={false} />  // for compact spaces
 */
export function Wordmark({
  withTrademark = true,
}: {
  withTrademark?: boolean;
} = {}): ReactElement {
  return (
    <>
      Auto<span>e</span>CTD
      {withTrademark && (
        <sup
          style={{
            fontSize: '0.45em',
            opacity: 0.7,
            marginLeft: '0.1em',
            // Anchor the ™ to the upper-right corner of the wordmark. The UA
            // default `vertical-align: super` floats it mid-height (reads as
            // low/centered); `top` + a tight line-height pins it to the cap
            // line. This matches the workspace header/sidebar CSS override,
            // which already uses `vertical-align: top`, so every context now
            // renders the mark consistently.
            verticalAlign: 'top',
            lineHeight: 1,
          }}
          aria-hidden="true"
        >
          ™
        </sup>
      )}
    </>
  );
}
